import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, ExternalLink, Star, AlertCircle, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { api } from "../services/api";
import DeliveryNav from "../components/delivery/DeliveryNav";

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function DeliveryAssinatura() {
  const { negocioId } = useParams();
  const [params] = useSearchParams();
  const [planos, setPlanos] = useState([]);
  const [assinatura, setAssinatura] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);
  const [negocioNome, setNegocioNome] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [pl, ass, biz] = await Promise.all([
        api.get(`/delivery/planos`),
        api.get(`/delivery/negocios/${negocioId}/assinatura`),
        api.get(`/businesses/${negocioId}`).catch(() => ({ data: null })),
      ]);
      setPlanos(pl.data?.items || []);
      setAssinatura(ass.data || null);
      setNegocioNome(biz.data?.nome || biz.data?.name || "");
    } catch (e) {
      toast.error("Erro ao carregar dados da assinatura");
    } finally {
      setLoading(false);
    }
  }, [negocioId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Mensagem de retorno do MP
  useEffect(() => {
    const status = params.get("status");
    if (status === "success" || status === "approved") {
      toast.success("Assinatura enviada — aguarde a confirmação do Mercado Pago.");
    } else if (status === "failure") {
      toast.error("Pagamento não foi concluído.");
    }
  }, [params]);

  const handleAssinar = async (plano) => {
    setSubmitting(plano.id);
    try {
      const r = await api.post(`/delivery/negocios/${negocioId}/assinatura`, {
        plano_id: plano.id,
      });
      if (r.data?.free) {
        toast.success("Plano gratuito ativado!");
        await refresh();
      } else if (r.data?.init_point) {
        window.location.href = r.data.init_point;
      } else {
        toast.error("Mercado Pago não retornou o link de pagamento");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar assinatura");
    } finally {
      setSubmitting(null);
    }
  };

  const handleCancelar = async () => {
    if (!window.confirm("Cancelar sua assinatura? Você manterá acesso até o fim do período pago.")) return;
    try {
      await api.post(`/delivery/negocios/${negocioId}/assinatura/cancelar`);
      toast.success("Assinatura cancelada");
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao cancelar");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  const statusAtual = assinatura?.status || "nenhuma";
  const ativa = assinatura?.active;
  const planoAtual = assinatura?.plano;

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12" data-testid="delivery-assinatura-page">
      <DeliveryNav negocioId={negocioId} negocioNome={negocioNome} />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-1">Assinatura</h1>
        <p className="text-sm text-[var(--text-secondary)] mb-6">
          Escolha o plano ideal para o seu Delivery. Cobrança recorrente via Mercado Pago.
        </p>

        {/* Status atual */}
        {assinatura && (
          <div
            className={`mb-6 rounded-xl border p-4 ${
              ativa
                ? "border-emerald-200 bg-emerald-50"
                : statusAtual === "pending"
                ? "border-amber-200 bg-amber-50"
                : "border-red-200 bg-red-50"
            }`}
            data-testid="delivery-assinatura-status"
            data-status={statusAtual}
          >
            <div className="flex items-start gap-3">
              {ativa ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              ) : (
                <AlertCircle className={`w-5 h-5 mt-0.5 ${statusAtual === "pending" ? "text-amber-600" : "text-red-600"}`} />
              )}
              <div className="flex-1">
                <p className="font-semibold">
                  {ativa && `Plano ${planoAtual?.nome || ""} ativo`}
                  {statusAtual === "pending" && "Pagamento em processamento"}
                  {statusAtual === "cancelled" && "Assinatura cancelada"}
                  {statusAtual === "expired" && "Assinatura vencida — pague para reativar"}
                  {statusAtual === "paused" && "Assinatura pausada"}
                  {statusAtual === "nenhuma" && "Nenhum plano ativo (modo trial)"}
                </p>
                {assinatura?.motivo && (
                  <p className="text-sm mt-1">{assinatura.motivo}</p>
                )}
                {assinatura?.valor > 0 && (
                  <p className="text-xs text-[var(--text-secondary)] mt-1">
                    {formatBRL(assinatura.valor)} / mês • Próxima cobrança:{" "}
                    {assinatura?.next_payment_date
                      ? new Date(assinatura.next_payment_date).toLocaleDateString("pt-BR")
                      : "—"}
                  </p>
                )}
                {assinatura?.init_point && !ativa && statusAtual === "pending" && (
                  <a
                    href={assinatura.init_point}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-2 text-sm text-[var(--primary)] hover:underline"
                    data-testid="delivery-assinatura-retomar"
                  >
                    Concluir pagamento <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                {ativa && !assinatura.free_plan && (
                  <button
                    onClick={handleCancelar}
                    className="mt-2 text-xs text-red-600 hover:underline"
                    data-testid="delivery-assinatura-cancelar"
                  >
                    Cancelar assinatura
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Lista de planos */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {planos.map((p) => {
            const isCurrent = planoAtual?.id === p.id && ativa;
            const destaque = p.destaque;
            return (
              <div
                key={p.id}
                className={`rounded-2xl border bg-white p-5 flex flex-col relative ${
                  destaque
                    ? "border-orange-400 ring-2 ring-orange-200 shadow-md"
                    : "border-[var(--border)]"
                }`}
                data-testid={`plano-card-${p.id}`}
              >
                {destaque && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 text-[10px] uppercase font-bold tracking-wide bg-orange-500 text-white rounded-full inline-flex items-center gap-1">
                    <Sparkles className="w-3 h-3" /> Recomendado
                  </span>
                )}
                <h3 className="text-lg font-bold">{p.nome}</h3>
                <p className="text-xs text-[var(--text-secondary)] mt-1 min-h-[36px]">
                  {p.descricao}
                </p>
                <p className="text-3xl font-extrabold mt-3 text-[var(--primary)]">
                  {p.preco > 0 ? formatBRL(p.preco) : "Grátis"}
                  {p.preco > 0 && (
                    <span className="text-sm text-[var(--text-secondary)] font-normal">
                      /mês
                    </span>
                  )}
                </p>
                <ul className="mt-4 space-y-1.5 text-sm flex-1">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    <span>
                      {p.max_produtos === null || p.max_produtos === undefined
                        ? "Produtos ilimitados"
                        : `Até ${p.max_produtos} produto${p.max_produtos === 1 ? "" : "s"}`}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {p.permite_complementos ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <span className="w-4 h-4 mt-0.5 shrink-0 text-[var(--text-secondary)]">—</span>
                    )}
                    <span className={p.permite_complementos ? "" : "text-[var(--text-secondary)]"}>
                      {p.permite_complementos ? "Complementos (recheios, tamanhos…)" : "Sem complementos"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {p.permite_encomendas ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <span className="w-4 h-4 mt-0.5 shrink-0 text-[var(--text-secondary)]">—</span>
                    )}
                    <span className={p.permite_encomendas ? "" : "text-[var(--text-secondary)]"}>
                      {p.permite_encomendas ? "Encomendas agendadas" : "Sem encomendas"}
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    {p.permite_cupons ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                    ) : (
                      <span className="w-4 h-4 mt-0.5 shrink-0 text-[var(--text-secondary)]">—</span>
                    )}
                    <span className={p.permite_cupons ? "" : "text-[var(--text-secondary)]"}>
                      {p.permite_cupons ? "Cupons de desconto" : "Sem cupons"}
                    </span>
                  </li>
                </ul>
                <button
                  onClick={() => handleAssinar(p)}
                  disabled={submitting === p.id || isCurrent}
                  className={`mt-5 w-full px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 ${
                    isCurrent
                      ? "bg-emerald-100 text-emerald-700 cursor-default"
                      : destaque
                      ? "bg-orange-500 text-white hover:bg-orange-600"
                      : "bg-[var(--primary)] text-white hover:opacity-90"
                  }`}
                  data-testid={`plano-assinar-${p.id}`}
                >
                  {submitting === p.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                  ) : isCurrent ? (
                    <span className="inline-flex items-center gap-1"><Star className="w-3.5 h-3.5" /> Plano atual</span>
                  ) : p.preco > 0 ? (
                    "Assinar"
                  ) : (
                    "Ativar grátis"
                  )}
                </button>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-xs text-[var(--text-secondary)] text-center">
          Pagamento processado pelo Mercado Pago. Você pode cancelar a qualquer momento.
        </p>
      </div>
    </div>
  );
}
